import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";

import AddCalendar from "./AddCalendar";
import AddMicrosoftCalendar from "./AddMicrosoftCalendar";
import Calendar from "./Calendar";
import CalendarDiagnostics from "./CalendarDiagnostics";

import { useCalendarConnectionsStore } from "../store";
import auth from "../Account/Auth";
import be from "../BE";

function CalendarConnections() {
  // Zustand store hooks
  const calendarsList = useCalendarConnectionsStore((s) => s.calendarsList);
  const connectedCalendars = useCalendarConnectionsStore(
    (s) => s.connectedCalendars
  );
  const maxCalendars = useCalendarConnectionsStore((s) => s.maxCalendars);
  const setCalendarsList = useCalendarConnectionsStore(
    (s) => s.setCalendarsList
  );
  const setConnectedCalendars = useCalendarConnectionsStore(
    (s) => s.setConnectedCalendars
  );
  const setMaxCalendars = useCalendarConnectionsStore((s) => s.setMaxCalendars);

  // Local state
  const [loading, setLoading] = useState(false);
  const [showTestRsvp, setShowTestRsvp] = useState(false);

  const { t } = useTranslation(["calendarConnections"]);
  const history = useHistory();
  const location = useLocation();

  // Helper: fetch calendars & update store + RSVP flag
  const fetchCalendars = async () => {
    setLoading(true);
    try {
      await auth.isAuthenticated();
      const response = await be.get(
        "calendarhandler",
        "/getCalendars",
        false,
        true
      );

      setCalendarsList(response.calendarsList || []);
      setConnectedCalendars(response.connectedCalendars || 0);
      setMaxCalendars(response.maxCalendars || 0);
      setShowTestRsvp(response.showTestRSVP === true);
    } catch (err) {
      if (err === "NOT_AUTHENTICATED") {
        history.push({ pathname: "/login" });
      } else {
        console.error("Error fetching calendars:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  // On mount, and when referrer changes
  useEffect(() => {
    document.title = "Calendbook - Calendar Connections";
    const qp = new URLSearchParams(location.search);
    const ref = qp.get("referrer");
    if (ref) console.log("referrer:", ref);

    fetchCalendars();
  }, [history, location.search]);

  // Connection‐problem detection
  const hasConnectionProblems =
    Array.isArray(calendarsList) &&
    calendarsList.some((cal) => cal.stopLight === false);

  // Show diagnostics panel if connection problems OR RSVP test needed
  const showDiagnostics = hasConnectionProblems || showTestRsvp;

  return (
    <Container className="mt-5">
      <Row>
        {/* ─── LEFT COLUMN (Calendars List) ─── */}
        <Col
          xs={12}
          md={showDiagnostics ? 8 : { span: 8, offset: 2 }}
          className="mb-4 mb-md-0"
        >
          <div style={{ marginTop: 20, marginBottom: 20 }}>
            <div style={{ display: "flex" }}>
              <div style={{ marginRight: 10, fontSize: 24, fontWeight: 600 }}>
                {t("connectedCalendars")}
              </div>
              <div
                style={{
                  fontSize: 18,
                  color: "var(--color-secondary)",
                  display: "flex",
                  margin: "auto 0",
                }}
              >
                {connectedCalendars}/{maxCalendars}
              </div>
              <div style={{ marginLeft: "auto" }}>
                <AddCalendar connectedCalendars={connectedCalendars} />
                <AddMicrosoftCalendar callback={fetchCalendars} />
              </div>
            </div>
            <small style={{ color: "var(--color-secondary)" }}>
              {t("subtitle")}
            </small>
          </div>

          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            calendarsList.map((calendar, index) => (
              <Calendar
                key={`calendar-connection-${calendar.email}`}
                index={index}
                fullName={calendar.fullName}
                email={calendar.email}
                pic={calendar.calendarPic}
                access={calendar.access}
                calendars={calendarsList}
                setCalendars={setCalendarsList}
                sync={calendar.sync}
                onCalendarDisconnected={fetchCalendars}
              />
            ))
          )}
        </Col>

        {/* ─── RIGHT COLUMN (Diagnostics) ─── */}
        {showDiagnostics && (
          <Col xs={12} md={4} className="mt-4 mt-md-0">
            <CalendarDiagnostics
              calendarsList={calendarsList}
              showTestRsvp={showTestRsvp}
              onRsvpConfirmed={fetchCalendars}
            />
          </Col>
        )}
      </Row>
    </Container>
  );
}

export default CalendarConnections;
